/**
 * SF-4665: fake data batches for one company. Thin hooks over Refine's useCustom /
 * useCustomMutation (the data provider unwraps the sf-api `{ success, data }` envelope, so the
 * inner payload is `query.data?.data`), same pattern as companyDeleteClient.ts.
 *
 *   GET  /admin/v1/companies/:id/demo-data/batches?limit&offset          newest first
 *   POST /admin/v1/companies/:id/demo-data/batches/:batchId/remove-impact
 *   POST /admin/v1/companies/:id/demo-data/batches/:batchId/remove        { allow_legacy? }
 *   POST /admin/v1/companies/:id/demo-data/remove-impact
 *   POST /admin/v1/companies/:id/demo-data/remove                         { confirm_name }
 * Every route needs admin:demos:create plus the company edit gate on sf-api.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';
import type { AxiosError } from 'axios';
import { COMPANIES_API_BASE } from './companyDeleteClient';

export type DemoBatchStatus = 'planned' | 'running' | 'done' | 'failed' | 'removing' | 'removed';

export interface DemoDataCounts {
  responses: number;
  tmp_responses: number;
  help_desk_responses: number;
  ratings: number;
  survey_sents: number;
  clients: number;
  recipient_infos: number;
  user_tasks: number;
  occurrence_overrides: number;
  kb_documents: number;
}

interface DemoBatchPerson {
  id: string;
  name: string | null;
  email: string | null;
}

/** One batch as sf-api returns it (the fields this panel reads). */
export interface DemoBatch {
  id: string;
  company_id: string;
  module: string;
  source: string;
  status: DemoBatchStatus;
  is_legacy: boolean;
  params: { start_date?: string; end_date?: string } | null;
  planned_total: number | null;
  created_total: number | null;
  created_by: DemoBatchPerson | null;
  created_at: string | null;
  started_at?: string | null;
  removed_at: string | null;
}

export interface BatchRemoveImpact {
  batch_id: string;
  is_legacy: boolean;
  counts: DemoDataCounts;
  total: number;
}

export interface CompanyRemoveImpact {
  company: { id: string; name: string; account_type: string };
  batch_ids: string[];
  counts: DemoDataCounts;
  total: number;
  legacy_batches_skipped: number;
}

/**
 * Batches per page. The data provider's `custom` drops `totalCount`, so the list pages with
 * limit/offset and offers a next page while the last read came back full.
 */
export const DEMO_BATCH_PAGE_SIZE = 50;

/** How often the list is read again while a batch is still being added or removed. */
export const DEMO_BATCH_POLL_MS = 5000;

/** Busy polling stops after this long; the panel then offers a Refresh button instead. */
export const DEMO_BATCH_POLL_LIMIT_MS = 5 * 60 * 1000;

/**
 * A batch planned or running for longer than this is treated as stuck: Remove is offered and
 * sf-api answers BATCH_BUSY if its job is in fact still alive.
 */
export const DEMO_BATCH_STUCK_MS = 30 * 60 * 1000;

const BUSY: DemoBatchStatus[] = ['planned', 'running', 'removing'];

export const isBatchBusy = (b: DemoBatch) => BUSY.includes(b.status);

/** Planned or running for over DEMO_BATCH_STUCK_MS (from when it started, else when it was made). */
export function isBatchStuck(b: DemoBatch, now: number): boolean {
  if (b.status !== 'planned' && b.status !== 'running') return false;
  const since = Date.parse(b.started_at ?? b.created_at ?? '');
  return Number.isFinite(since) && now - since > DEMO_BATCH_STUCK_MS;
}

/**
 * True once busy polling has run for DEMO_BATCH_POLL_LIMIT_MS without every batch settling.
 * Measured against the clock (`now`), not the last successful read, so failing reads stop too.
 */
export const batchesPollExpired = (busySince: number | null, now: number) =>
  busySince !== null && now - busySince >= DEMO_BATCH_POLL_LIMIT_MS;

/**
 * Keep reading while any batch is busy, for at most DEMO_BATCH_POLL_LIMIT_MS of continuous busy
 * polling (`busySince` is when the current busy spell was first seen); stop once every batch has
 * a final status.
 */
export const batchesRefetchInterval = (
  batches: DemoBatch[] | undefined,
  busySince: number | null = null,
  now = Date.now(),
) =>
  (batches ?? []).some(isBatchBusy) && !batchesPollExpired(busySince, now)
    ? DEMO_BATCH_POLL_MS
    : false;

const companyBase = (companyId: string) =>
  `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/demo-data`;

/**
 * Key prefix for every page of a company's batches, so a remove can refresh the list (and its
 * polling) straight away.
 */
export const demoBatchesQueryKey = (companyId: string) => ['sf-admin', 'demo-batches', companyId];

export function useDemoBatches(
  companyId: string,
  offset: number,
  enabled: boolean,
  busySince: number | null = null,
) {
  return useCustom<DemoBatch[]>({
    url: `${companyBase(companyId)}/batches`,
    method: 'get',
    config: { query: { limit: DEMO_BATCH_PAGE_SIZE, offset } },
    queryOptions: {
      queryKey: [...demoBatchesQueryKey(companyId), offset],
      enabled: enabled && !!companyId,
      keepPreviousData: false,
      // Asked after every read with the cached list. Polling also stops when the panel unmounts
      // (no observer left), so nothing is read or written after leaving the page.
      // The time bound uses the clock: a run of failed reads must stop as well.
      refetchInterval: (data) => batchesRefetchInterval(data?.data, busySince, Date.now()),
      refetchIntervalInBackground: false,
    },
    errorNotification: false,
  });
}

/** What removing one batch would delete. Read when its confirm dialog opens. */
export function useBatchRemoveImpact(companyId: string, batchId: string | undefined) {
  return useCustom<BatchRemoveImpact>({
    url: `${companyBase(companyId)}/batches/${encodeURIComponent(batchId ?? '')}/remove-impact`,
    method: 'post',
    config: { payload: {} },
    queryOptions: {
      queryKey: ['sf-admin', 'demo-batch-remove-impact', companyId, batchId ?? ''],
      enabled: !!companyId && !!batchId,
      keepPreviousData: false,
      // Never show last time's counts when a dialog is reopened.
      cacheTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    },
    errorNotification: false,
  });
}

/** What removing all of the company's (non-legacy) fake data would delete. */
export function useCompanyRemoveImpact(companyId: string, enabled: boolean) {
  return useCustom<CompanyRemoveImpact>({
    url: `${companyBase(companyId)}/remove-impact`,
    method: 'post',
    config: { payload: {} },
    queryOptions: {
      queryKey: ['sf-admin', 'demo-company-remove-impact', companyId],
      enabled: enabled && !!companyId,
      keepPreviousData: false,
      // Never show last time's counts when a dialog is reopened.
      cacheTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    },
    errorNotification: false,
  });
}

export const useDemoDataMutation = useCustomMutation;

/** `allow_legacy` goes only with a legacy batch: nothing else may ever send it. */
export function removeBatchArgs(companyId: string, batch: Pick<DemoBatch, 'id' | 'is_legacy'>) {
  return {
    url: `${companyBase(companyId)}/batches/${encodeURIComponent(batch.id)}/remove`,
    method: 'post' as const,
    values: batch.is_legacy ? { allow_legacy: true } : {},
    errorNotification: false as const,
    successNotification: false as const,
  };
}

export function removeAllArgs(companyId: string, confirmName: string) {
  return {
    url: `${companyBase(companyId)}/remove`,
    method: 'post' as const,
    values: { confirm_name: confirmName },
    errorNotification: false as const,
    successNotification: false as const,
  };
}

export const MODULE_LABELS: Record<string, string> = {
  survey: 'Survey',
  audit: 'Audit',
  mystery_visit: 'Mystery visit',
  helpdesk: 'Helpdesk',
  client_connect: 'Client connect',
  ratings: 'Ratings',
  tasks: 'Tasks',
  knowledge_base: 'Knowledge base',
};

export const SOURCE_LABELS: Record<string, string> = {
  admin_app: 'Admin app',
  eva_chat: 'Eva chat',
  demo_builder: 'Demo builder',
  cron: 'Scheduled top-up',
  script: 'Script',
};

export const STATUS_LABELS: Record<DemoBatchStatus, string> = {
  planned: 'Planned',
  running: 'Adding',
  done: 'Done',
  failed: 'Failed',
  removing: 'Removing',
  removed: 'Removed',
};

export const COUNT_LABELS: Record<keyof DemoDataCounts, string> = {
  responses: 'Responses',
  tmp_responses: 'Unfinished responses',
  help_desk_responses: 'Helpdesk responses',
  ratings: 'Ratings',
  survey_sents: 'Surveys sent',
  clients: 'Clients',
  recipient_infos: 'Recipient details',
  user_tasks: 'Tasks',
  occurrence_overrides: 'Task occurrences',
  kb_documents: 'Knowledge base documents',
};

/** Fixed copy per sf-api refusal code. Server text is never shown. */
const REFUSAL_MESSAGES: Record<string, string> = {
  PERMISSION_DENIED: 'You need the "Create prospect demos" permission to do this.',
  NO_ACCESS: 'You cannot change this company.',
  NOT_FOUND: 'That fake data batch no longer exists. Refresh the page.',
  NOT_DEMO_OR_TEST: 'Fake data can only be used on demo or test companies.',
  WRONG_COMPANY: 'That survey or location belongs to another company.',
  NO_SURVEY: 'This company has no suitable active survey.',
  TOO_MANY_RECORDS: 'That would add too many records (the limit is 20,000).',
  INVALID_INPUT: 'Some of the details are not valid.',
  LEGACY_BATCH: 'This is a legacy batch. It can only be removed here, one at a time.',
  CONFIRM_NAME_MISMATCH: 'The name you typed does not match the company name.',
  BATCH_BUSY: 'This fake data is still being added or removed. Try again when it has finished.',
  ALREADY_REMOVED: 'That fake data was already removed. Refresh the page.',
  TOO_MANY_BATCHES:
    'This company has more than 1,000 fake data batches, too many to remove at once. Remove them one batch at a time.',
  MODULE_OFF: 'That module is switched off for this company. Switch it on to add its fake data.',
};

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

export function demoDataErrorMessage(error: unknown, fallback = FALLBACK_MESSAGE): string {
  const response = (error as AxiosError<{ code?: string; reason_code?: string }> | undefined)
    ?.response;
  const code = response?.data?.code;
  if (code && REFUSAL_MESSAGES[code]) return REFUSAL_MESSAGES[code];
  // sf-api's company edit gate answers 403 with a reason_code and no code.
  if (response?.data?.reason_code === 'COMPANY_EDIT_NOT_ALLOWED') return REFUSAL_MESSAGES.NO_ACCESS;
  if (response?.status === 403) return REFUSAL_MESSAGES.PERMISSION_DENIED;
  return fallback;
}
